import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from "react-router";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {AgGridReact} from "ag-grid-react";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import {getCities, getNeighborhoods, getStreets} from "../../../../utils/location-utils.js";
import optionsUtils from "../../../../utils/options-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import ModalAddress from "../../../../components/modal/modal-address.jsx";
import {useQuery} from "@tanstack/react-query";
import capturePlacesService from "../../../../services/web/capture-places-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import Swal from "sweetalert2";
import MySwal from "../../../../components/swal/my-swal.jsx";
import Alert from "../../../../components/alert/alert.jsx";

const BASENAME = import.meta.env.BASE_URL;

const initialState = {
    mobile: false,
    description: null,
    group: null,
    zipcode: null,
    address: null,
    number: null,
    neighborhood: null,
    city: null,
    latitude: null,
    longitude: null,
}
const CapturePlacesForm = () => {
    const navigate = navigateHook();

    const {id} = useParams()
    const [map, setMap] = useState(false);
    const [state, setState] = useState(initialState)
    const [lanes, setLanes] = useState([])
    const gridRef = useRef(null);
    const capturePlaceSnapshotRef = useRef(null);
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const columnDefs = [
        {headerName: "Ativas", field: "activeLanes", flex: 1},
        {headerName: "Inativas", field: "inactiveLanes", flex: 1}
    ];

    const { refetch, isLoading } = useQuery({
        queryFn: () =>  capturePlacesService.getById(id).then((data) => {
            setLanes([{
                activeLanes: data.activeLanes,
                inactiveLanes: data.inactiveLanes,
            }])
            setState(data.capturePlace)
            capturePlaceSnapshotRef.current = structuredClone(data.capturePlace)
            return data
        }),
        queryKey: ['capture-places'],
        enabled: !!id,
    });

    const handleInputs = (id, value) => setState(prev => ({...prev, [id]: value}));

    const onChange = ({target: {value, id}}) => setState(prev => ({...prev, [id]: value}))

    const updateLatLng = (val) => {
        setState(prev => ({...prev, latitude: val.lat(), longitude: val.lng()}))
    };

    const save = async () => {
        if((!state.latitude && !state.longitude) && !state.mobile){
            projectUtils.swalErrorMessage("", 'Os campos "Latitude" e "Longitude" são obrigatórios.', "Ok");
            return;
        }

        const isCoordinatesValid = validateLatLong()
        if (!isCoordinatesValid) {
            projectUtils.swalErrorMessage("", '"Latitude" ou "Longitude" inválida.', "Ok");
            return;
        }

        const keysToCheck = [
            "description",
            "group",
            "zipcode",
            "address",
            "number",
            "neighborhood",
            "city",
            "latitude",
            "longitude",
            "mobile",
            "code"
        ];
        const hasChanges = id && keysToCheck.some(key => state[key] !== capturePlaceSnapshotRef.current?.[key]);

        if (hasChanges) {
            const {isConfirmed} = await MySwal.fire({
                title: "Atenção",
                icon: "warning",
                html: (
                    <div className="flex items-center justify-center flex-col gap-2">
                        <span>Se o ponto de coleta mudou de lugar, o ideal é cadastrar um novo ponto de coleta.</span>
                        <span>Para continuar, digite <strong>continuar</strong> abaixo:</span>
                    </div>
                ),
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                cancelButtonText: "Cancelar",
                input: "text",
                confirmButtonColor: "#063A6C",
                reverseButtons: true,
                preConfirm(inputValue) {
                    if (inputValue.trim() !== "continuar") {
                        Swal.showValidationMessage("Digite corretamente a palavra-chave");
                    }
                }
            });

            if (!isConfirmed) return;
        }

        const preparedState = {
            ...(id && { code: state.code,  }),
            mobile: state.mobile ?? false,
            description: state.description ?? null,
            group: state.group ?? null,
            zipcode: state.zipcode ?? null,
            address: state.address ?? null,
            number: state.number ?? null,
            neighborhood: state.neighborhood ?? null,
            city: state.city ?? null,
            latitude: `${state.latitude ?? null}`,
            longitude: `${state.longitude ?? null}` ,
        }

        const response = id ? capturePlacesService.edit(preparedState) : capturePlacesService.create(preparedState);
        response
            .then((data) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                navigateToEdit(data)
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                const errData = error.response?.data;

                if (errData) {
                    message = Array.isArray(errData) ? translateError(errData[0]) : translateError(errData);
                }

                projectUtils.swalErrorMessage("", message, "Ok", false, 'Não', undefined, {htmlContainer: 'whitespace-pre-line'});
            })
    }

    const validateLatLong = () => {
        if (state.mobile || (!state.latitude || !state.longitude)) {
            return true;
        }

        const regexCoordinates = /^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/gi;

        return regexCoordinates.test(`${state.latitude},${state.longitude}`);
    }

    const translateError = (message)  => {
        if (message instanceof Object && message.name.includes("invalid.latlon.city")) {
            const valueDecode = JSON.parse(message.value);
            const decodedString = valueDecode.foundCity ? `Os valores informados pertencem a ${valueDecode.foundCity}` : ""
            return `As coordenadas (latitude e longitude) não correspondem com o município selecionado. \n${decodedString}`;
        }

        switch (message) {
            case 'descriptionPcl.already.registered':
                return `Este "Nome" já está sendo utilizado.`;
            case 'missing.description':
                return 'O campo "Nome" é obrigatório.';
            case 'missing.address':
                return 'O campo "Logradouro" é obrigatório.';
            case 'missing.city':
                return 'O campo "Município" é obrigatório.';
            case 'missing.latitude':
                return 'O campo "Latitude" é obrigatório.';
            case 'missing.longitude':
                return 'O campo "Longitude" é obrigatório.';
            case 'pcl.mobile.cannotChange':
                return 'Não é possível alterar "Mobile" de um ponto já cadastrado.';
            case 'captureplace.has.lanes':
                return 'Ponto de coleta possui câmeras associadas.';
            default:
                return 'Não foi possível salvar.';
        }
    }

    const destroy = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o ponto "${state.description}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                capturePlacesService.destroy(id).then(() => {
                    navigateToList()
                    projectUtils.success(true, "Excluído com sucesso")
                })
            }
        });
    }

    const newCapture = () => {
        let isBlank = false;
        let urlDev = `/capture-places/form`;
        navigate(urlDev, isBlank);
    }

    const navigateToEdit = (data) => {
        let isBlank = false;
        let urlDev = `/capture-places/form/${data}`;
        navigate(urlDev, isBlank);
    }

    const navigateToList = () => {
        let isBlank = false;
        let urlDev = `/capture-places`;
        navigate(urlDev, isBlank);
    }

    const onCellClicked = (data) => {
        const searchLanes = data.colDef.field === "activeLanes" ? data.data.activeLanes : data.data.inactiveLanes

        if (searchLanes){
            localStorage.setItem('lanes', searchLanes);
            window.open(`${BASENAME}lanes`);
        }
    }

    const openMap = () => setMap(true);

    useEffect(() => {
        if (state.mobile) {
            setState(prev => ({
                ...prev,
                address: "",
                number: "",
                neighborhood: "",
                city: "",
                latitude: "",
                longitude: "",
            }) )
        }
    }, [state.mobile]);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Ponto de coleta
                </Template.Title>
                {
                    id &&
                    <>
                        <Button target={"primary-outline"} onClick={() => newCapture()}>
                            <FaPlus/>
                            Adicionar ponto
                        </Button>
                    </>
                }
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-1">
                                <InputSwitch
                                    id="mobile"
                                    label="Móvel"
                                    checked={!!state.mobile}
                                    disabled={!!id}
                                    onChange={(checked) => setState(prev => ({ ...prev, mobile: checked }))}
                                />
                            </div>
                            <div className="col-span-7">
                                <InputForm id="description" value={state.description} onChange={onChange}
                                           label="Nome *"/>
                            </div>
                            <div className="col-span-4">
                                <InputForm id="group" value={state.group} onChange={onChange}
                                           label="Grupo de pontos de coleta (SentryView)"/>
                            </div>

                            <div className="col-span-4">
                                <InputForm id="zipcode" value={state.zipcode} onChange={onChange} mask={MaskDefault.cep}
                                           label="CEP"/>
                            </div>
                            <div className="col-span-6">
                                <SelectComponent
                                    loadOptions={getStreets}
                                    value={optionsUtils.getValue(state?.address)}
                                    isAsync={true}
                                    label={"Logradouro *"}
                                    onChange={(selected) => handleInputs("address", selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                    isDisabled={state.mobile}
                                />
                            </div>
                            <div className="col-span-2">
                                <InputForm id="number" value={state.number} onChange={onChange} type={"number"}
                                           label="Número" disabled={state.mobile}/>
                            </div>

                            <div className="col-span-6">
                                <SelectComponent
                                    isDisabled={state.mobile}
                                    loadOptions={getNeighborhoods}
                                    value={optionsUtils.getValue(state?.neighborhood)}
                                    isAsync={true}
                                    label={"Bairro"}
                                    onChange={(selected) => handleInputs("neighborhood", selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                />
                            </div>
                            <div className="col-span-6">
                                <SelectComponent
                                    isDisabled={state.mobile}
                                    loadOptions={getCities}
                                    value={optionsUtils.getValue(state?.city)}
                                    isAsync={true}
                                    label={"Município *"}
                                    onChange={(selected) => handleInputs("city", selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                />
                            </div>

                            <InputForm
                                disabled={state.mobile}
                                id={"latitude"}
                                label={"Latitude *"}
                                classNameWrapper={"col-span-5"}
                                value={state.latitude}
                                onChange={onChange}
                            />

                            <InputForm
                                disabled={state.mobile}
                                id={"longitude"}
                                label={"Longitude *"}
                                classNameWrapper={"col-span-5"}
                                value={state.longitude}
                                onChange={onChange}
                            />

                            <Button target={"primary"} className={"col-span-2"} onClick={() => openMap()} disabled={state.mobile}>
                                Mapa
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                {id &&
                    <Card className={"mt-2"}>
                        <Card.Header>
                            <Card.Title>Câmeras</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="col-span-6 flex">
                                <div className={"h-[83px] w-full p-0 ag-theme-quartz"}>
                                    <AgGridReact
                                        ref={gridRef}
                                        rowHeight={40}
                                        rowData={lanes}
                                        columnDefs={columnDefs}
                                        animateRows={false}
                                        suppressCellFocus={true}
                                        onCellClicked={onCellClicked}
                                        gridOptions={{localeText: aggridUtils.localization()}}
                                        theme={theme}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                }
                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="w-full flex justify-end">
                            <div className="flex gap-2">
                                {
                                    (lanes[0]?.activeLanes || lanes[0]?.inactiveLanes) &&
                                    <Alert target="warning" className={"flex h-[32px] items-center"}>Não é possível excluir, pois existem câmeras dependentes do ponto.</Alert>
                                }
                                {
                                    id &&
                                    <>
                                        <Button target="danger" onClick={destroy} disabled={lanes[0]?.activeLanes || lanes[0]?.inactiveLanes} actionFeedbackType='delete'>

                                            Excluir
                                        </Button>
                                    </>
                                }


                                <Button onClick={save} actionFeedbackType='save'>

                                    Salvar
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>

            <ModalAddress
                open={map}
                setOpen={setMap}
                address={state}
                changeLocation={(value) => updateLatLng(value)}
            />

        </Template>
    );
};

export default CapturePlacesForm;